import { Component } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { switchMap, catchError, of } from 'rxjs';
import { calendarIcon } from 'src/app/app-icons';
import { TrainerModel } from 'src/app/models/trainer.model';
import { TrainingAnonymousModel } from 'src/app/models/training/training-anonymous.model';
import { TrainingLocationTypeModel } from 'src/app/models/training/training-location-type.model';
import { FileUtilService } from 'src/app/services/file-util.service';
import { LoadingService } from 'src/app/services/loading.service';
import { TrainingAnonymousService } from 'src/app/services/training-anonymous.service';

@Component({
  selector: 'app-trainer-details',
  standalone: false,
  templateUrl: './trainer-details.component.html',
  styleUrl: './trainer-details.component.scss'
})
export class TrainerDetailsComponent {
  trainingKey: string;
  accessToken: string;
  TrainingLocationType = TrainingLocationTypeModel;
  training: TrainingAnonymousModel | null = null;
  location: { latitude: number; longitude: number; location: string } | null = null;
  trainers: TrainerModel[] = [];

  isAddedToCalendar: boolean = false;
  isDeclined: boolean = false;

  isInvalidInvitation: boolean = false;
  isTrainingNotFound: boolean = false;

  showDeclineInvitationModal: boolean = false 
  
  get locationType() {
    switch (this.training?.locationType) {
      case TrainingLocationTypeModel.Address:
        return this.translateService.instant(
          'COMMON.FIELDS.EVENT-TYPES.FACE-TO-FACE'
        );
      case TrainingLocationTypeModel.Web:
        return this.translateService.instant(
          'COMMON.FIELDS.EVENT-TYPES.ONLINE'
        );
      case TrainingLocationTypeModel.GoToWebinar:
        return 'GoToWebinar';
      case TrainingLocationTypeModel.Webex:
        return 'Webex';
      case TrainingLocationTypeModel.MsTeamsWebinar:
        return 'MsTeams';
      case TrainingLocationTypeModel.MsTeamsMeetings:
        return 'MsTeamsMeetings';
      default:
        return '';
    }
  }

  get durationString() {
    if (
      !this.training ||
      typeof this.training?.iddTime !== 'number' ||
      isNaN(this.training.iddTime)
    ) {
      return '00:00 h';
    }

    const hours = Math.floor(this.training?.iddTime / 60);
    const remainingMinutes = this.training?.iddTime % 60;
    const durationString = `${hours}:${
      remainingMinutes < 10 ? '0' : ''
    }${remainingMinutes} h`;

    return durationString;
  }

  constructor(
    private route: ActivatedRoute,
    private trainingAnonymousService: TrainingAnonymousService,
    private loadingService: LoadingService,
    public translateService: TranslateService,
    private toastrService: ToastrService,
    private sanitized: DomSanitizer,
    private router: Router,
    private fileUtilService: FileUtilService,
  ) {
    this.trainingKey = this.route.snapshot.params['key'];
    this.accessToken = this.route.snapshot.params['token'];
    
    this.loadTrainingData(this.accessToken);
  }

  getCalendarIcon() {
    return this.sanitized.bypassSecurityTrustHtml(calendarIcon);
  }

  loadTrainingData(accessToken: string | null): void {
    this.loadingService
      .load(
        this.trainingAnonymousService.getTrainingAnonymous(this.trainingKey, accessToken)
          .pipe(
            catchError(() => of(null))
          )
      )
      .pipe(
        switchMap((training) => {
          if (!training) {
            this.isTrainingNotFound = true;
            return of(false);
          }

          this.training = training;

          if(!this.training.currentTrainer){
            this.isInvalidInvitation = true;
          }

          if (training.latitude && training.longitude) {
            this.location = {
              latitude: training.latitude,
              longitude: training.longitude,
              location: training.location
            };
          };

          const admin = this.training.admin;
          this.trainers = [
            admin,
            ...this.training.trainers.filter(t => t.id !== admin.id)
          ];

          if(!accessToken) {
            return of(false);
          }

          return this.loadingService.load(
            this.trainingAnonymousService.getIsAddedToCalendar(this.trainingKey, accessToken)
          );
        })
      )
      .subscribe((isAddedToCalendar) => {
        this.isAddedToCalendar = isAddedToCalendar;
      });
  }

  saveToCalendar() {
    this.loadingService.load(
      this.trainingAnonymousService.addToCalendar(this.trainingKey, this.accessToken)
    ).subscribe(() => {
      this.toastrService.success(
        this.translateService.instant(
          'MY-TRAININGS.SAVED-TO-CALENDAR-SUCCESSFUL'
        )
      );
      this.isAddedToCalendar = true;
      this.onDownloadIcsClick();
    });
  }


  onDownloadIcsClick(): void {
    this.loadingService
      .load(
        this.trainingAnonymousService.getTrainingAnonymousIcs(this.trainingKey, this.accessToken)
      )
      .subscribe({
        next: (ics) => this.fileUtilService.downloadFile(ics),
        error: () =>
          this.toastrService.error(
            this.translateService.instant(
              'COMMON.AN-ERROR-HAS-OCCURRED'
            )
          ),
      });
  }

  openDeclineTrainingConfirmationModal() {
    this.showDeclineInvitationModal = true;
  }

  openTraining(): void {
    const url = this.router.serializeUrl(
      this.router.createUrlTree(['my-trainings', 'training', this.training!.id])
    );
    window.open(url, '_blank');
  }

  decline() {
    this.loadingService
      .load(
        this.trainingAnonymousService.declineTrainerInvitation(
          this.trainingKey, this.accessToken
        )
      )
      .subscribe(() => {
        this.showDeclineInvitationModal = false;
        this.isDeclined = true;

        this.toastrService.success(
          this.translateService.instant(
            'TRAINER-DETAILS.DECLINED-INVITATION-SUCCESSFULLY'
          )
        );

        this.loadTrainingData(null);
      });
  }
}
